import { useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { useActivationEventAssets } from '@/hooks/useActivationEvents';
import {
  useMediaGenerationCatalog,
  resolveCatalog,
  matchModelCapability,
  catalogModelLabel,
} from '@/hooks/useMediaGenerationCatalog';
import type { ModelCapabilityResponse } from '@/types/generated/activations.generated';
import {
  CAPTURE_MODE_LABELS,
  DELIVERY_MODE_LABELS,
  MEDIA_TYPE_LABELS,
  PIAPI_CUSTOM_MODEL,
  defaultPhotoBoothVariant,
  seedVideoVariantDefaults,
  type ActivationCaptureMode,
  type ActivationDeliveryMode,
  type ActivationMediaType,
  type ActivationType,
  type BlazePodConfig,
  type ConfigErrors,
  type PhotoBoothConfig,
  type PhotoBoothVariant,
  type RaffleConfig,
  type RaffleOutcome,
  type TypedActivationConfig,
} from '@/types/activations';

interface ActivationConfigFormProps {
  /** The activation type the editor renders the typed config for. */
  type: ActivationType;
  /** Current typed config value. */
  value: TypedActivationConfig;
  onChange: (value: TypedActivationConfig) => void;
  /** Inline validation errors keyed by field name (from validateTypedConfig). */
  errors?: ConfigErrors;
  /** Event id, used by PhotoBooth to load selectable input assets. */
  eventId: string;
  /** Optional id prefix to keep inputs unique when several forms render together. */
  idPrefix?: string;
  disabled?: boolean;
}

/**
 * REQUIRED, strongly-typed per-activation-type config editor. Produces the EXACT
 * canonical config document (camelCase keys, string enums) the backend handler
 * validates, with no slugifying. The generic SchemaForm/SchemaBuilder remains for
 * OPTIONAL branding/copy only and is never the source of this required config.
 */
export function ActivationConfigForm({
  type,
  value,
  onChange,
  errors = {},
  eventId,
  idPrefix = 'cfg',
  disabled,
}: ActivationConfigFormProps) {
  if (type === 'BlazePod') {
    return (
      <BlazePodConfigForm
        value={value as BlazePodConfig}
        onChange={onChange}
        errors={errors}
        idPrefix={idPrefix}
        disabled={disabled}
      />
    );
  }
  if (type === 'RaffleDraw') {
    return (
      <RaffleConfigForm
        value={value as RaffleConfig}
        onChange={onChange}
        errors={errors}
        idPrefix={idPrefix}
        disabled={disabled}
      />
    );
  }
  return (
    <PhotoBoothConfigForm
      value={value as PhotoBoothConfig}
      onChange={onChange}
      errors={errors}
      eventId={eventId}
      idPrefix={idPrefix}
      disabled={disabled}
    />
  );
}

function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null;
  return (
    <p className="text-xs text-destructive" data-testid={`config-error-${id}`}>
      {message}
    </p>
  );
}

// ── BlazePod ────────────────────────────────────────────────────────────────

function BlazePodConfigForm({
  value,
  onChange,
  errors,
  idPrefix,
  disabled,
}: {
  value: BlazePodConfig;
  onChange: (v: BlazePodConfig) => void;
  errors: ConfigErrors;
  idPrefix: string;
  disabled?: boolean;
}) {
  const set = <K extends keyof BlazePodConfig>(key: K, v: BlazePodConfig[K]) =>
    onChange({ ...value, [key]: v });

  return (
    <div className="space-y-4" data-testid="blazepod-config-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-durationSeconds`}>
            Round duration (seconds)<span className="text-destructive"> *</span>
          </Label>
          <Input
            id={`${idPrefix}-durationSeconds`}
            data-testid="config-durationSeconds"
            type="number"
            min={1}
            value={Number.isFinite(value.durationSeconds) ? String(value.durationSeconds) : ''}
            disabled={disabled}
            onChange={(e) =>
              set('durationSeconds', e.target.value === '' ? 0 : Number(e.target.value))
            }
          />
          <FieldError id="durationSeconds" message={errors.durationSeconds} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-leaderboardSize`}>
            Leaderboard size<span className="text-destructive"> *</span>
          </Label>
          <Input
            id={`${idPrefix}-leaderboardSize`}
            data-testid="config-leaderboardSize"
            type="number"
            min={1}
            value={Number.isFinite(value.leaderboardSize) ? String(value.leaderboardSize) : ''}
            disabled={disabled}
            onChange={(e) =>
              set('leaderboardSize', e.target.value === '' ? 0 : Number(e.target.value))
            }
          />
          <FieldError id="leaderboardSize" message={errors.leaderboardSize} />
        </div>
      </div>
    </div>
  );
}

// ── RaffleDraw ──────────────────────────────────────────────────────────────

const RAFFLE_OUTCOMES: { value: RaffleOutcome; label: string }[] = [
  { value: 'PrizeDraw', label: 'Prize draw (random winners)' },
  { value: 'Leaderboard', label: 'Leaderboard (ranked by score)' },
];

function RaffleConfigForm({
  value,
  onChange,
  errors,
  idPrefix,
  disabled,
}: {
  value: RaffleConfig;
  onChange: (v: RaffleConfig) => void;
  errors: ConfigErrors;
  idPrefix: string;
  disabled?: boolean;
}) {
  const set = <K extends keyof RaffleConfig>(key: K, v: RaffleConfig[K]) =>
    onChange({ ...value, [key]: v });

  return (
    <div className="space-y-4" data-testid="raffle-config-form">
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-outcome`}>
          Outcome<span className="text-destructive"> *</span>
        </Label>
        <Select
          value={value.outcome}
          disabled={disabled}
          onValueChange={(v) => set('outcome', v as RaffleOutcome)}
        >
          <SelectTrigger id={`${idPrefix}-outcome`} data-testid="config-outcome">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RAFFLE_OUTCOMES.map((o) => (
              <SelectItem key={o.value} value={o.value} data-testid={`config-outcome-${o.value}`}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <FieldError id="outcome" message={errors.outcome} />
      </div>

      {value.outcome === 'PrizeDraw' && (
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-numberOfWinners`}>
            Number of winners<span className="text-destructive"> *</span>
          </Label>
          <Input
            id={`${idPrefix}-numberOfWinners`}
            data-testid="config-numberOfWinners"
            type="number"
            min={1}
            value={Number.isFinite(value.numberOfWinners) ? String(value.numberOfWinners) : ''}
            disabled={disabled}
            onChange={(e) =>
              set('numberOfWinners', e.target.value === '' ? 0 : Number(e.target.value))
            }
          />
          <FieldError id="numberOfWinners" message={errors.numberOfWinners} />
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-prizeName`}>Prize name</Label>
          <Input
            id={`${idPrefix}-prizeName`}
            data-testid="config-prizeName"
            value={value.prizeName ?? ''}
            placeholder="e.g. Noise-cancelling headphones"
            disabled={disabled}
            onChange={(e) => set('prizeName', e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-prizeNameAr`}>Prize name (Arabic)</Label>
          <Input
            id={`${idPrefix}-prizeNameAr`}
            data-testid="config-prizeNameAr"
            dir="rtl"
            value={value.prizeNameAr ?? ''}
            disabled={disabled}
            onChange={(e) => set('prizeNameAr', e.target.value)}
          />
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Switch
            id={`${idPrefix}-captureName`}
            data-testid="config-captureName"
            checked={value.captureName}
            disabled={disabled}
            onCheckedChange={(checked) => set('captureName', checked)}
          />
          <Label htmlFor={`${idPrefix}-captureName`}>Capture participant name (lead)</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id={`${idPrefix}-captureEmail`}
            data-testid="config-captureEmail"
            checked={value.captureEmail}
            disabled={disabled}
            onCheckedChange={(checked) => set('captureEmail', checked)}
          />
          <Label htmlFor={`${idPrefix}-captureEmail`}>Capture participant email (lead)</Label>
        </div>
      </div>
    </div>
  );
}

// ── PhotoBooth ──────────────────────────────────────────────────────────────

function PhotoBoothConfigForm({
  value,
  onChange,
  errors,
  eventId,
  idPrefix,
  disabled,
}: {
  value: PhotoBoothConfig;
  onChange: (v: PhotoBoothConfig) => void;
  errors: ConfigErrors;
  eventId: string;
  idPrefix: string;
  disabled?: boolean;
}) {
  const { data: assets } = useActivationEventAssets(eventId);
  // The model dropdown is sourced from the API-served capability catalog (ADR-0036). Fetch it ONCE
  // here (the variant rows' parent) and pass the resolved list down — never per row. A failed/empty
  // catalog falls back to the built-in list so an outage cannot brick the form.
  const catalogQuery = useMediaGenerationCatalog();
  const { models, isLoading: catalogLoading } = resolveCatalog(catalogQuery);
  const set = <K extends keyof PhotoBoothConfig>(key: K, v: PhotoBoothConfig[K]) =>
    onChange({ ...value, [key]: v });

  const selectableAssets = useMemo(() => assets ?? [], [assets]);

  const variants = value.variants ?? [];
  const setVariants = (next: PhotoBoothVariant[]) => set('variants', next);
  const updateVariant = (index: number, patch: Partial<PhotoBoothVariant>) =>
    setVariants(variants.map((v, i) => (i === index ? { ...v, ...patch } : v)));
  const addVariant = () => setVariants([...variants, defaultPhotoBoothVariant()]);
  const removeVariant = (index: number) =>
    setVariants(variants.filter((_, i) => i !== index));
  const moveVariant = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= variants.length) return;
    const next = [...variants];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    setVariants(next);
  };

  const toggleAsset = (assetId: string) => {
    const exists = value.inputAssetIds.includes(assetId);
    set(
      'inputAssetIds',
      exists
        ? value.inputAssetIds.filter((id) => id !== assetId)
        : [...value.inputAssetIds, assetId],
    );
  };

  const captureModes: ActivationCaptureMode[] = ['Kiosk', 'OperatorCurated'];
  const deliveryModes: ActivationDeliveryMode[] = ['AutoSend', 'HoldForApproval'];

  return (
    <div className="space-y-4" data-testid="photobooth-config-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-captureMode`}>
            Capture mode<span className="text-destructive"> *</span>
          </Label>
          <Select
            value={value.captureMode}
            disabled={disabled}
            onValueChange={(v) => set('captureMode', v as ActivationCaptureMode)}
          >
            <SelectTrigger id={`${idPrefix}-captureMode`} data-testid="config-captureMode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {captureModes.map((m) => (
                <SelectItem key={m} value={m} data-testid={`config-captureMode-${m}`}>
                  {CAPTURE_MODE_LABELS[m]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {value.captureMode === 'OperatorCurated' && (
          <div className="space-y-1.5">
            <Label htmlFor={`${idPrefix}-deliveryMode`}>
              Delivery mode<span className="text-destructive"> *</span>
            </Label>
            <Select
              value={value.deliveryMode}
              disabled={disabled}
              onValueChange={(v) => set('deliveryMode', v as ActivationDeliveryMode)}
            >
              <SelectTrigger id={`${idPrefix}-deliveryMode`} data-testid="config-deliveryMode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {deliveryModes.map((m) => (
                  <SelectItem key={m} value={m} data-testid={`config-deliveryMode-${m}`}>
                    {DELIVERY_MODE_LABELS[m]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>
            Output variants<span className="text-destructive"> *</span>
          </Label>
          <span className="text-xs text-muted-foreground" data-testid="config-variant-count">
            {variants.length} variant{variants.length === 1 ? '' : 's'}
          </span>
        </div>
        <p className="text-xs text-muted-foreground">
          Each variant produces one output. Choose its media type, model and prompt.
        </p>
        {variants.length === 0 && (
          <p className="text-xs text-muted-foreground" data-testid="config-variants-empty">
            No variants yet. Add at least one.
          </p>
        )}
        <div className="space-y-3">
          {variants.map((variant, index) => (
            <VariantEditor
              key={index}
              index={index}
              total={variants.length}
              variant={variant}
              models={models}
              catalogLoading={catalogLoading}
              idPrefix={idPrefix}
              disabled={disabled}
              errors={errors}
              onChange={(patch) => updateVariant(index, patch)}
              onRemove={() => removeVariant(index)}
              onMove={(delta) => moveVariant(index, delta)}
            />
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={addVariant}
          data-testid="config-variant-add"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add variant
        </Button>
        <FieldError id="variants" message={errors.variants} />
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-generationCount`}>
            Generations / participant<span className="text-destructive"> *</span>
          </Label>
          <Input
            id={`${idPrefix}-generationCount`}
            data-testid="config-generationCount"
            type="number"
            min={1}
            value={Number.isFinite(value.generationCount) ? String(value.generationCount) : ''}
            disabled={disabled}
            onChange={(e) =>
              set('generationCount', e.target.value === '' ? 0 : Number(e.target.value))
            }
          />
          <FieldError id="generationCount" message={errors.generationCount} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-pollIntervalMs`}>Poll interval (ms)</Label>
          <Input
            id={`${idPrefix}-pollIntervalMs`}
            data-testid="config-pollIntervalMs"
            type="number"
            min={0}
            value={Number.isFinite(value.pollIntervalMs) ? String(value.pollIntervalMs) : ''}
            disabled={disabled}
            onChange={(e) =>
              set('pollIntervalMs', e.target.value === '' ? 0 : Number(e.target.value))
            }
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-maxPollAttempts`}>Max poll attempts</Label>
          <Input
            id={`${idPrefix}-maxPollAttempts`}
            data-testid="config-maxPollAttempts"
            type="number"
            min={0}
            value={Number.isFinite(value.maxPollAttempts) ? String(value.maxPollAttempts) : ''}
            disabled={disabled}
            onChange={(e) =>
              set('maxPollAttempts', e.target.value === '' ? 0 : Number(e.target.value))
            }
          />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-instructions`}>Participant instructions</Label>
          <Textarea
            id={`${idPrefix}-instructions`}
            data-testid="config-instructions"
            rows={2}
            value={value.instructions ?? ''}
            disabled={disabled}
            onChange={(e) => set('instructions', e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-instructionsAr`}>Instructions (Arabic)</Label>
          <Textarea
            id={`${idPrefix}-instructionsAr`}
            data-testid="config-instructionsAr"
            dir="rtl"
            rows={2}
            value={value.instructionsAr ?? ''}
            disabled={disabled}
            onChange={(e) => set('instructionsAr', e.target.value)}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Input assets</Label>
        {selectableAssets.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="config-assets-empty">
            No event assets uploaded yet. Upload assets on the Assets tab to use them as
            generation inputs.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="config-assets">
            {selectableAssets.map((asset) => {
              const selected = value.inputAssetIds.includes(asset.id);
              return (
                <button
                  type="button"
                  key={asset.id}
                  disabled={disabled}
                  onClick={() => toggleAsset(asset.id)}
                  data-testid={`config-asset-${asset.id}`}
                  aria-pressed={selected}
                  className="focus:outline-none focus:ring-2 focus:ring-ring rounded-md"
                >
                  <Badge variant={selected ? 'default' : 'outline'} className="cursor-pointer">
                    {asset.label || asset.assetKind}
                  </Badge>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── PhotoBooth variant editor ────────────────────────────────────────────────

const MEDIA_TYPES: ActivationMediaType[] = ['Image', 'Video'];

function VariantEditor({
  index,
  total,
  variant,
  models,
  catalogLoading,
  idPrefix,
  disabled,
  errors,
  onChange,
  onRemove,
  onMove,
}: {
  index: number;
  total: number;
  variant: PhotoBoothVariant;
  /** Model capability catalog (API-served, or the built-in fallback) — passed from the parent. */
  models: ModelCapabilityResponse[];
  /** True while the catalog is still loading — the model select shows a loading affordance. */
  catalogLoading: boolean;
  idPrefix: string;
  disabled?: boolean;
  errors: ConfigErrors;
  onChange: (patch: Partial<PhotoBoothVariant>) => void;
  onRemove: () => void;
  onMove: (delta: number) => void;
}) {
  const matched = matchModelCapability(models, variant);
  const isCustom = matched === null;
  const [showAspectRatio, setShowAspectRatio] = useState(!!variant.aspectRatio);

  // Capability-driven field visibility (master §0.2): a catalog model that declares it accepts a
  // negative prompt / aspect ratio reveals that field. The custom (no-match) path keeps full manual
  // control over the aspect ratio (today's behaviour) but never offers the negative prompt.
  const showNegativePrompt = !!matched?.supportsNegativePrompt;
  const showAspectRatioField = isCustom || !!matched?.supportsAspectRatio;

  // Options for the model dropdown filtered to the variant's media type.
  const modelOptions = models.filter((o) => o.mediaType === variant.mediaType);
  // The currently-selected dropdown value: a catalog taskType or the custom sentinel.
  const modelValue = matched ? matched.taskType ?? PIAPI_CUSTOM_MODEL : PIAPI_CUSTOM_MODEL;

  // Capability adjustments applied when the selected model changes: a model that cannot accept a
  // value has it CLEARED to `undefined` so JSON serialization drops it (§4.36 / §4.36a).
  const capabilityPatch = (entry: ModelCapabilityResponse): Partial<PhotoBoothVariant> => ({
    aspectRatio: entry.supportsAspectRatio
      ? entry.defaultAspectRatio ?? variant.aspectRatio ?? ''
      : undefined,
    ...(entry.supportsNegativePrompt ? {} : { negativePrompt: undefined }),
  });

  const onMediaTypeChange = (mediaType: ActivationMediaType) => {
    // Pick the first catalog model for the new media type (or fall back to custom-friendly blank).
    const firstForType = models.find((o) => o.mediaType === mediaType);
    const base: Partial<PhotoBoothVariant> = firstForType
      ? {
          mediaType,
          provider: firstForType.provider ?? '',
          model: firstForType.model ?? '',
          taskType: firstForType.taskType ?? '',
          ...capabilityPatch(firstForType),
        }
      : { mediaType };
    // Becoming Video → seed the two-step (Gemini → Kling) pre-video defaults.
    if (mediaType === 'Video') {
      onChange({ ...base, ...seedVideoVariantDefaults(variant) });
    } else {
      onChange(base);
    }
  };

  const onModelChange = (selected: string) => {
    if (selected === PIAPI_CUSTOM_MODEL) {
      // Reveal free-text by clearing model/taskType so they no longer match a catalog entry.
      // Custom is not a catalog model, so it cannot support a negative prompt — drop any value.
      onChange({ model: '', taskType: '', negativePrompt: undefined });
      return;
    }
    const option = models.find((o) => o.taskType === selected);
    if (option) {
      onChange({
        provider: option.provider ?? '',
        model: option.model ?? '',
        taskType: option.taskType ?? '',
        ...capabilityPatch(option),
      });
    }
  };

  return (
    <div className="rounded-md border p-3 space-y-3" data-testid={`config-variant-${index}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-muted-foreground">Variant {index + 1}</span>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            disabled={disabled || index === 0}
            onClick={() => onMove(-1)}
            aria-label={`Move variant ${index + 1} up`}
            data-testid={`config-variant-up-${index}`}
          >
            <ArrowUp className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            disabled={disabled || index === total - 1}
            onClick={() => onMove(1)}
            aria-label={`Move variant ${index + 1} down`}
            data-testid={`config-variant-down-${index}`}
          >
            <ArrowDown className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-destructive"
            disabled={disabled}
            onClick={onRemove}
            aria-label={`Remove variant ${index + 1}`}
            data-testid={`config-variant-remove-${index}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-variant-${index}-mediaType`}>Media type</Label>
          <Select
            value={variant.mediaType}
            disabled={disabled}
            onValueChange={(v) => onMediaTypeChange(v as ActivationMediaType)}
          >
            <SelectTrigger
              id={`${idPrefix}-variant-${index}-mediaType`}
              data-testid={`config-variant-mediaType-${index}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MEDIA_TYPES.map((m) => (
                <SelectItem
                  key={m}
                  value={m}
                  data-testid={`config-variant-mediaType-${index}-${m}`}
                >
                  {MEDIA_TYPE_LABELS[m]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-variant-${index}-model`}>
            Model<span className="text-destructive"> *</span>
          </Label>
          <Select
            value={modelValue}
            disabled={disabled || catalogLoading}
            onValueChange={onModelChange}
          >
            <SelectTrigger
              id={`${idPrefix}-variant-${index}-model`}
              data-testid={`config-variant-model-${index}`}
            >
              <SelectValue placeholder={catalogLoading ? 'Loading models…' : 'Select a model'} />
            </SelectTrigger>
            <SelectContent>
              {modelOptions.map((o) => (
                <SelectItem
                  key={o.taskType ?? o.model ?? ''}
                  value={o.taskType ?? ''}
                  data-testid={`config-variant-model-${index}-${o.taskType}`}
                >
                  {catalogModelLabel(o)}
                </SelectItem>
              ))}
              <SelectItem
                value={PIAPI_CUSTOM_MODEL}
                data-testid={`config-variant-model-${index}-custom`}
              >
                Custom…
              </SelectItem>
            </SelectContent>
          </Select>
          {catalogLoading && (
            <p
              className="text-xs text-muted-foreground"
              data-testid={`config-variant-model-loading-${index}`}
            >
              Loading models…
            </p>
          )}
          <FieldError id={`variant-${index}-model`} message={errors[`variant-${index}-model`]} />
        </div>
      </div>

      {isCustom && (
        <div className="grid gap-3 sm:grid-cols-2" data-testid={`config-variant-custom-${index}`}>
          <div className="space-y-1.5">
            <Label htmlFor={`${idPrefix}-variant-${index}-customModel`}>
              Custom model<span className="text-destructive"> *</span>
            </Label>
            <Input
              id={`${idPrefix}-variant-${index}-customModel`}
              data-testid={`config-variant-customModel-${index}`}
              value={variant.model}
              placeholder="e.g. gemini"
              disabled={disabled}
              onChange={(e) => onChange({ model: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${idPrefix}-variant-${index}-customTaskType`}>Task type</Label>
            <Input
              id={`${idPrefix}-variant-${index}-customTaskType`}
              data-testid={`config-variant-customTaskType-${index}`}
              value={variant.taskType}
              placeholder="e.g. gemini-2.5-flash-image"
              disabled={disabled}
              onChange={(e) => onChange({ taskType: e.target.value })}
            />
          </div>
        </div>
      )}

      {variant.mediaType === 'Video' && (
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-variant-${index}-preVideoPrompt`}>
            Pre-video image prompt (Gemini)<span className="text-destructive"> *</span>
          </Label>
          <p className="text-xs text-muted-foreground">
            Gemini composes a still from the captured photo + input assets using this prompt; the
            video model (Kling) then animates that composite.
          </p>
          <Textarea
            id={`${idPrefix}-variant-${index}-preVideoPrompt`}
            data-testid={`config-variant-preVideoPrompt-${index}`}
            value={variant.preVideoPrompt ?? ''}
            rows={3}
            placeholder="Describe the still Gemini should compose (costume/scene, brand logo placement, keep likeness)…"
            disabled={disabled}
            onChange={(e) => onChange({ preVideoPrompt: e.target.value })}
          />
          <FieldError
            id={`variant-${index}-preVideoPrompt`}
            message={errors[`variant-${index}-preVideoPrompt`]}
          />
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-variant-${index}-prompt`}>
          {variant.mediaType === 'Video' ? 'Video prompt (Kling)' : 'Prompt'}
          <span className="text-destructive"> *</span>
        </Label>
        <Textarea
          id={`${idPrefix}-variant-${index}-prompt`}
          data-testid={`config-variant-prompt-${index}`}
          value={variant.prompt}
          rows={2}
          placeholder={
            variant.mediaType === 'Video'
              ? 'How the composite still should animate (motion, camera)…'
              : `Prompt for variant ${index + 1}`
          }
          disabled={disabled}
          onChange={(e) => onChange({ prompt: e.target.value })}
        />
        <FieldError id={`variant-${index}-prompt`} message={errors[`variant-${index}-prompt`]} />
      </div>

      {showNegativePrompt && (
        <div className="space-y-1.5" data-testid={`config-variant-negativePrompt-wrap-${index}`}>
          <Label htmlFor={`${idPrefix}-variant-${index}-negativePrompt`}>Negative prompt</Label>
          <p className="text-xs text-muted-foreground">
            What the model should avoid (e.g. extra fingers, text artifacts, blurriness).
          </p>
          <Textarea
            id={`${idPrefix}-variant-${index}-negativePrompt`}
            data-testid={`config-variant-negativePrompt-${index}`}
            value={variant.negativePrompt ?? ''}
            rows={2}
            placeholder="e.g. blurry, distorted hands, watermark, text"
            disabled={disabled}
            onChange={(e) =>
              onChange({ negativePrompt: e.target.value === '' ? undefined : e.target.value })
            }
          />
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-variant-${index}-shareCaption`}>Social share caption</Label>
        <p className="text-xs text-muted-foreground">
          Pre-filled caption shared with the photo/video to Instagram, Facebook, LinkedIn.
        </p>
        <Textarea
          id={`${idPrefix}-variant-${index}-shareCaption`}
          data-testid={`config-variant-shareCaption-${index}`}
          value={variant.shareCaption ?? ''}
          rows={2}
          placeholder="e.g. Loved the AI photo booth at #AcmeExpo!"
          disabled={disabled}
          onChange={(e) => onChange({ shareCaption: e.target.value })}
        />
      </div>

      {showAspectRatioField && (
        <div className="space-y-1.5" data-testid={`config-variant-aspectRatio-wrap-${index}`}>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-1 text-xs text-muted-foreground"
            disabled={disabled}
            onClick={() => setShowAspectRatio((s) => !s)}
            data-testid={`config-variant-aspectRatio-toggle-${index}`}
          >
            {showAspectRatio ? (
              <ChevronDown className="mr-1 h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="mr-1 h-3.5 w-3.5" />
            )}
            Aspect ratio
          </Button>
          {showAspectRatio && (
            <Input
              data-testid={`config-variant-aspectRatio-${index}`}
              value={variant.aspectRatio ?? ''}
              placeholder="e.g. 1:1, 16:9"
              disabled={disabled}
              onChange={(e) => onChange({ aspectRatio: e.target.value })}
            />
          )}
        </div>
      )}
    </div>
  );
}
